import type { EditableScenarioProperties } from '@/components/adaptive-planner/scenarios/scenario-card/radar-chart/lib/types';
import { tempoApi } from '@/lib/api/tempoApi';
import type {
  LastPublishResultWrapper,
  PublishScenarioResponse,
  ScenarioGenerationStatus,
  ScenarioResponse,
} from '@/lib/api/types/adaptive-planner/scenario';

const getScenarios = async ({ planId }: { planId: number }) => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/scenarios?includeMetrics=true`)
    .json<ScenarioResponse[]>();
  return response;
};

const getScenario = async ({ planId, scenarioId }: { planId: number; scenarioId: number }) => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/scenarios/${scenarioId}?includeMetrics=true`)
    .json<ScenarioResponse>();
  return response;
};

const updateScenario = async ({
  scenarioId,
  planId,
  data,
}: {
  scenarioId: number;
  planId: number;
  data: EditableScenarioProperties;
}): Promise<ScenarioResponse> => {
  const response = await tempoApi()
    .patch(`api/v1/adaptive-planner/plans/${planId}/scenarios/${scenarioId}`, {
      json: data,
    })
    .json<ScenarioResponse>();
  return response;
};

const generateScenarios = async ({ planId }: { planId: number }): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/plans/${planId}/scenarios/generate`).json();
};

const regenerateScenarios = async ({ planId }: { planId: number }): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/plans/${planId}/scenarios/regenerate`).json();
};

const publishScenario = async ({
  planId,
  scenarioId,
}: {
  planId: number;
  scenarioId: number;
}): Promise<PublishScenarioResponse> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/scenarios/${scenarioId}/publish`)
    .json<PublishScenarioResponse>();
  return response;
};

const resetScenario = async ({
  planId,
  scenarioId,
}: {
  planId: number;
  scenarioId: number;
}): Promise<void> => {
  await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/scenarios/${scenarioId}/reset`)
    .json();
};

const rescheduleScenario = async ({
  planId,
  scenarioId,
}: {
  planId: number;
  scenarioId: number;
}): Promise<void> => {
  await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/scenarios/${scenarioId}/schedule`)
    .json();
};

const getLastPublishResult = async ({
  planId,
  scenarioId,
}: {
  planId: number;
  scenarioId: number;
}): Promise<LastPublishResultWrapper> => {
  return await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/scenarios/${scenarioId}/last-publish`)
    .json<LastPublishResultWrapper>();
};

const getScenariosDataSet = async ({ planId }: { planId: number }): Promise<number[]> => {
  return await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/scenarios/data-set`)
    .json<number[]>();
};

const getScenarioGenerationStatus = async ({
  planId,
  scenarioId,
}: {
  planId: number;
  scenarioId: number;
}): Promise<ScenarioGenerationStatus> => {
  return await tempoApi()
    .put(`api/v1/adaptive-planner/plans/${planId}/scenarios/${scenarioId}/state`)
    .json<ScenarioGenerationStatus>();
};

export const AdaptivePlannerScenarioApi = {
  generateScenarios,
  getScenarios,
  getScenario,
  updateScenario,
  publishScenario,
  resetScenario,
  rescheduleScenario,
  regenerateScenarios,
  getLastPublishResult,
  getScenariosDataSet,
  getScenarioGenerationStatus,
} as const;
